import { t } from "../translate/i18n";

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export function formatSince(iso, now = new Date()) {
  // new Date(null) é 1970 (válido): sem início conhecido não há "desde".
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  if (isSameDay(d, now)) return time;
  return `${d.toLocaleDateString([], { day: "2-digit", month: "2-digit" })} ${time}`;
}

// Nomes vêm do tenant: concatenados fora do i18n para não expandir "{{...}}".
export function crossConnectionChipLabel(entry) {
  const others = entry?.others || [];
  if (others.length === 0) return "";
  // Rótulo curto de propósito: a fileira do atendimento tem uma faixa só e a
  // conexão/atendente de cada atendimento seguem no tooltip (sentences).
  return t("crossConnection.chipPrefix");
}

export function crossConnectionSentence(other, now = new Date()) {
  if (!other) return "";
  const attendant = other.attendantName || t("crossConnection.unknownAttendant");
  const connection = other.whatsappName || t("crossConnection.unknownConnection");
  const since = formatSince(other.since, now);
  const base = `${attendant} ${t("crossConnection.isServingOn")} ${connection}`;
  return since ? `${base} ${t("crossConnection.since")} ${since}` : base;
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function formatRelativeHours(iso, now = new Date()) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Relógio do cliente adiantado/atrasado não deve gerar "há -3 min".
  const diff = Math.max(0, now.getTime() - d.getTime());
  if (diff < MINUTE_MS) return t("crossConnection.agoNow");
  if (diff < HOUR_MS) return t("crossConnection.agoMinutes", { count: Math.floor(diff / MINUTE_MS) });
  if (diff < DAY_MS) return t("crossConnection.agoHours", { count: Math.floor(diff / HOUR_MS) });
  const days = Math.floor(diff / DAY_MS);
  return days === 1 ? t("crossConnection.agoOneDay") : t("crossConnection.agoDays", { count: days });
}

export function lastClosedChipLabel(lastClosed, now = new Date()) {
  if (!lastClosed) return "";
  const ago = formatRelativeHours(lastClosed.closedAt, now);
  // Rótulo curto de propósito: mesma faixa única da lista; a conexão vai no tooltip.
  return ago
    ? `${t("crossConnection.lastClosedChip")} ${ago}`
    : t("crossConnection.lastClosedChip");
}

export function lastClosedSentence(lastClosed, now = new Date()) {
  if (!lastClosed) return "";
  const attendant = lastClosed.attendantName || t("crossConnection.unknownAttendant");
  const connection = lastClosed.whatsappName || t("crossConnection.unknownConnection");
  const ago = formatRelativeHours(lastClosed.closedAt, now);
  const base = `${attendant} · ${connection}`;
  return ago ? `${base} · ${t("crossConnection.closedWord")} ${ago}` : base;
}

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

export function formatDealValue(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return BRL.format(value);
}

// Motivo/tipo/produto são cadastros do tenant: rótulo traduzido + valor concatenado fora do i18n.
export function lastClosedDetails(lastClosed) {
  if (!lastClosed) return { tags: [], meta: "" };
  const tags = [];
  if (lastClosed.closureReason) {
    tags.push(`${t("crossConnection.reasonLabel")}: ${lastClosed.closureReason}`);
  }
  if (lastClosed.demandType) {
    tags.push(`${t("crossConnection.demandLabel")}: ${lastClosed.demandType}`);
  }
  const meta = [lastClosed.relatedProduct, formatDealValue(lastClosed.dealValue)]
    .filter(Boolean)
    .join(" · ");
  return { tags, meta };
}
